<script setup lang="ts">
import type { RegisterRequest } from '@/js/account/shared/model';
import { computed, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import signUpImage from '@/images/account/sign-up.jpg';
import { AuthRouteName } from '@/js/account/app/router/route-names';
import AuthTitle from '@/js/account/components/auth/AuthTitle.vue';
import BaseButton from '@/js/account/components/base/BaseButton.vue';
import BaseCheckbox from '@/js/account/components/base/BaseCheckbox.vue';
import BaseField from '@/js/account/components/base/BaseField.vue';
import BasePhoneField from '@/js/account/components/base/BasePhoneField.vue';
import BaseSelect from '@/js/account/components/base/BaseSelect.vue';
import { navigateAfterAuth, signUp } from '@/js/account/entities/auth';
import AuthLayout from '@/js/account/layouts/auth-layout/AuthLayout.vue';
import { PRIVACY_POLICY_URL, useFormErrors } from '@/js/account/shared/lib';
import { SOURCE_OF_DISCOVERY_OPTIONS } from '@/js/account/shared/model';
import { classNames } from '@/js/lib/bem';

const router = useRouter();
const route = useRoute();
const {
  clearErrors,
  clearFieldOnInput,
  setFromAxiosError,
  setGeneralError,
  fieldError,
  hasFieldError,
  generalError,
} = useFormErrors();

const cn = classNames('register-page');

const form = reactive<RegisterRequest>({
  name: '',
  email: '',
  phone: '',
  whatsApp: '',
  sourceOfDiscovery: '',
  password: '',
});

const termsAccepted = ref(false);
const termsTouched = ref(false);
const submitting = ref(false);

const termsError = computed(() => {
  if (!termsTouched.value) {
    return undefined;
  }

  if (!termsAccepted.value) {
    return 'You must accept the Privacy Policy and Terms of use.';
  }

  return undefined;
});

async function onSubmit(): Promise<void> {
  clearErrors();
  termsTouched.value = true;

  if (!termsAccepted.value) {
    return;
  }

  submitting.value = true;

  const payload: RegisterRequest = {
    name: form.name.trim(),
    email: form.email.trim(),
    password: form.password,
    phone: form.phone?.trim() || null,
    whatsApp: form.whatsApp?.trim() || null,
    sourceOfDiscovery: form.sourceOfDiscovery?.trim() || null,
  };

  try {
    await signUp(payload);

    const result = await navigateAfterAuth(router, route);

    if (result === 'session-unavailable') {
      setGeneralError('Could not load your session. Please try again.');
    }
  } catch (error) {
    setFromAxiosError(error);
  } finally {
    submitting.value = false;
  }
}

function onTermsUpdate(value: boolean): void {
  termsAccepted.value = value;
  termsTouched.value = true;
  clearFieldOnInput('termsAccepted');
}
</script>

<template>
  <AuthLayout :src="signUpImage" alt="">
    <div :class="cn()">
      <AuthTitle :class="cn('title')" title="Sign up">
        <template #description>
          <p :class="cn('description')">
            Already have an account?
            <RouterLink :to="{ name: AuthRouteName.Login }" :class="cn('link')">
              Sign in
            </RouterLink>
          </p>
        </template>
      </AuthTitle>

      <form :class="cn('form')" @submit.prevent="onSubmit">
        <BaseField
          v-model="form.name"
          :class="cn('field')"
          label="Name"
          name="name"
          autocomplete="name"
          :invalid="hasFieldError('name')"
          :message="fieldError('name')"
          required
          @update:model-value="clearFieldOnInput('name')"
        />
        <BaseField
          v-model="form.email"
          :class="cn('field')"
          label="Email"
          name="email"
          type="email"
          autocomplete="email"
          :invalid="hasFieldError('email') || !!generalError"
          :message="
            fieldError('email') || (generalError ? generalError : undefined)
          "
          required
          @update:model-value="
            clearFieldOnInput('email', { clearGeneral: true })
          "
        />
        <BasePhoneField
          v-model="form.phone"
          :class="cn('field')"
          label="Phone"
          name="phone"
          :invalid="hasFieldError('phone')"
          :message="fieldError('phone')"
          required
          @update:model-value="clearFieldOnInput('phone')"
        />
        <BasePhoneField
          v-model="form.whatsApp"
          :class="cn('field')"
          label="WhatsApp"
          name="whatsApp"
          :invalid="hasFieldError('whatsApp')"
          :message="fieldError('whatsApp')"
          @update:model-value="clearFieldOnInput('whatsApp')"
        />
        <BaseSelect
          v-model="form.sourceOfDiscovery"
          :class="cn('field')"
          name="sourceOfDiscovery"
          :options="SOURCE_OF_DISCOVERY_OPTIONS"
          placeholder="How did you find us?"
          :invalid="hasFieldError('sourceOfDiscovery')"
          :message="fieldError('sourceOfDiscovery')"
          @update:model-value="clearFieldOnInput('sourceOfDiscovery')"
        />
        <BaseField
          v-model="form.password"
          :class="cn('field')"
          label="Password"
          name="password"
          type="password"
          autocomplete="new-password"
          :invalid="hasFieldError('password')"
          :message="fieldError('password')"
          required
          @update:model-value="clearFieldOnInput('password')"
        />

        <div :class="cn('terms')">
          <BaseCheckbox
            :model-value="termsAccepted"
            name="termsAccepted"
            :invalid="!!termsError"
            required
            @update:model-value="onTermsUpdate"
          >
            I agree with
            <a
              :href="PRIVACY_POLICY_URL"
              :class="cn('terms-link')"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy and Terms of use
            </a>
          </BaseCheckbox>
          <p v-if="termsError" :class="cn('terms-error')" role="alert">
            {{ termsError }}
          </p>
        </div>

        <BaseButton
          :class="cn('button')"
          type="submit"
          full-width
          :loading="submitting"
        >
          Sign up
        </BaseButton>
      </form>
    </div>
  </AuthLayout>
</template>

<style lang="scss" scoped>
.register-page {
  max-width: 363px;
  width: 100%;

  &__form {
    display: flex;
    flex-direction: column;
  }

  &__field {
    margin-bottom: 24px;
  }

  &__terms {
    margin-bottom: 24px;
    font-family: var(--font-family-sans);
    font-size: 14px;
    line-height: 1.4;
    color: var(--color-text-secondary);
  }

  &__terms-link {
    color: var(--color-primary);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__terms-error {
    margin: 8px 0 0;
    font-size: 12px;
    color: var(--base-field-error-color, #db1515);
  }

  &__button {
    margin-top: 24px;
  }

  &__title {
    text-align: center;
  }

  &__description {
    text-align: center;
    font-family: var(--font-family-sans);
    font-weight: var(--font-weight-regular);
    font-size: 16px;
    line-height: 1.125;
    color: var(--color-text-secondary);
    margin: 0;
  }

  &__link {
    color: var(--color-primary);
    text-decoration: none;
    font-weight: var(--font-weight-regular);
    font-family: var(--font-family-sans);
    font-size: 16px;
    line-height: 1.125;
  }
}
</style>
